import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx-js-style';
import {
    X,
    Check,
    Clock,
    Users,
    Calendar,
    Search,
    Save,
    Loader2,
    CheckCheck,
    Activity,
    FileText,
    ShieldAlert,
    LayoutGrid,
    CalendarDays,
    UserCheck,
    TrendingUp,
    FileSpreadsheet
} from 'lucide-react';
import './TeamAttendanceHistoryModal.css';
import API_BASE_URL from '../../config/api';

const STATUS_CONFIG = {
    1: { label: 'Presente', icon: Check, className: 'status-present', color: '#10b981' },
    2: { label: 'Falta', icon: X, className: 'status-absent', color: '#ef4444' },
    3: { label: 'Atrasado', icon: Clock, className: 'status-late', color: '#f59e0b' },
    4: { label: 'Lesionado', icon: ShieldAlert, className: 'status-injured', color: '#64748b' },
    5: { label: 'Justificado', icon: FileText, className: 'status-excused', color: '#0284c7' },
    0: { label: 'Por Marcar', icon: null, className: 'status-empty', color: '#cbd5e1' },
};

const TeamAttendanceHistoryModal = ({
    isOpen,
    onClose,
    teamId,
    athletes = [],
    teamName = '',
    sportName = '',
    season = '',
    readOnly = false,
    embedded = false
}) => {
    const [history, setHistory] = useState({ trainings: [], attendance: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modifiedRecords, setModifiedRecords] = useState({}); // { 'eventId_athleteId': status }
    const [viewMode, setViewMode] = useState('matrix'); // 'matrix' | 'session' | 'athletes'
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSessionId, setSelectedSessionId] = useState(null);

    const containerRef = useRef(null);
    const sessionScrollRef = useRef(null);

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/attendance/team-history/${teamId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setHistory(data);

                // Auto-select closest training for session view
                if (data.trainings && data.trainings.length > 0) {
                    const today = new Date();
                    let closest = data.trainings[0];
                    let minDiff = Math.abs(new Date(closest.startDateTime) - today);

                    data.trainings.forEach(t => {
                        const diff = Math.abs(new Date(t.startDateTime) - today);
                        if (diff < minDiff) {
                            minDiff = diff;
                            closest = t;
                        }
                    });
                    setSelectedSessionId(closest.id);
                }
            }
        } catch (err) {
            console.error('Error fetching attendance history:', err);
        } finally {
            setLoading(false);
        }
    }, [teamId]);

    useEffect(() => {
        if ((isOpen || embedded) && teamId) {
            fetchHistory();
            setModifiedRecords({});
            setSearchQuery('');
            setSelectedMonth('all');
        }
    }, [isOpen, embedded, teamId, fetchHistory]);

    // Auto-scroll to closest date in matrix view
    useEffect(() => {
        if (!loading && history.trainings.length > 0 && containerRef.current && viewMode === 'matrix') {
            const today = new Date();
            let closestIndex = 0;
            let minDiff = Infinity;

            const currentFilteredTrainings = history.trainings.filter(t => {
                if (selectedMonth === 'all') return true;
                const d = new Date(t.startDateTime);
                const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                return mKey === selectedMonth;
            });

            currentFilteredTrainings.forEach((t, index) => {
                const diff = Math.abs(new Date(t.startDateTime) - today);
                if (diff < minDiff) {
                    minDiff = diff;
                    closestIndex = index;
                }
            });

            const timer = setTimeout(() => {
                if (!containerRef.current) return;
                const headers = containerRef.current.querySelectorAll('th.training-col-header');
                const targetHeader = headers[closestIndex];
                if (targetHeader) {
                    const stickyHeader = containerRef.current.querySelector('th.sticky-col');
                    const stickyWidth = stickyHeader ? stickyHeader.offsetWidth : 160;
                    const targetLeft = targetHeader.offsetLeft - stickyWidth - 16;
                    containerRef.current.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
                }
            }, 250);
            return () => clearTimeout(timer);
        }
    }, [loading, history.trainings, selectedMonth, viewMode]);

    // Unique months present in trainings
    const availableMonths = useMemo(() => {
        const set = new Set();
        (history.trainings || []).forEach(t => {
            const d = new Date(t.startDateTime);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            set.add(key);
        });
        return Array.from(set).sort().reverse();
    }, [history.trainings]);

    // Filtered trainings
    const filteredTrainings = useMemo(() => {
        return (history.trainings || []).filter(t => {
            if (selectedMonth === 'all') return true;
            const d = new Date(t.startDateTime);
            const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            return mKey === selectedMonth;
        });
    }, [history.trainings, selectedMonth]);

    // Filtered athletes
    const filteredAthletes = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return athletes;
        return athletes.filter(a =>
            a.name?.toLowerCase().includes(query) ||
            (a.jerseyNumber && a.jerseyNumber.toString().includes(query)) ||
            (a.position && a.position.toLowerCase().includes(query))
        );
    }, [athletes, searchQuery]);

    const getStatus = useCallback((eventId, athleteId) => {
        const key = `${eventId}_${athleteId}`;
        if (modifiedRecords[key] !== undefined) return modifiedRecords[key];

        const record = history.attendance.find(a => a.eventId === eventId && a.athleteId === athleteId);
        return record ? record.status : 0;
    }, [modifiedRecords, history.attendance]);

    const isRecordModified = (eventId, athleteId) => {
        const key = `${eventId}_${athleteId}`;
        return modifiedRecords[key] !== undefined;
    };

    // Cycle through main statuses: 0 -> 1 -> 2 -> 3 -> 5 -> 1
    const handleStatusCycle = (eventId, athleteId) => {
        const currentStatus = getStatus(eventId, athleteId);
        let nextStatus = 1;
        if (currentStatus === 1) nextStatus = 2;
        else if (currentStatus === 2) nextStatus = 3;
        else if (currentStatus === 3) nextStatus = 5;
        else if (currentStatus === 5) nextStatus = 1;
        else if (currentStatus === 4) nextStatus = 1;

        setModifiedRecords(prev => ({
            ...prev,
            [`${eventId}_${athleteId}`]: nextStatus
        }));
    };

    const handleSetExplicitStatus = (eventId, athleteId, status) => {
        setModifiedRecords(prev => ({
            ...prev,
            [`${eventId}_${athleteId}`]: status
        }));
    };

    // Bulk action: Mark all athletes present for this event
    const handleMarkAllPresent = (eventId) => {
        const updates = {};
        filteredAthletes.forEach(a => {
            updates[`${eventId}_${a.id}`] = 1;
        });
        setModifiedRecords(prev => ({
            ...prev,
            ...updates
        }));
    };

    // A training is "realized" if at least one athlete has an attendance status recorded (s !== 0)
    const isTrainingRealized = useCallback((t) => {
        return athletes.some(a => getStatus(t.id, a.id) !== 0);
    }, [athletes, getStatus]);

    const realizedTrainings = useMemo(() => {
        return (history.trainings || []).filter(isTrainingRealized);
    }, [history.trainings, isTrainingRealized]);

    // Calculations
    const athleteStatsMap = useMemo(() => {
        const map = {};
        const activeTrainings = selectedMonth === 'all'
            ? history.trainings
            : history.trainings.filter(t => {
                const d = new Date(t.startDateTime);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === selectedMonth;
            });

        const activeRealized = activeTrainings.filter(isTrainingRealized);
        const seasonRealizedCount = realizedTrainings.length;

        athletes.forEach(athlete => {
            let present = 0;
            let absent = 0;
            let late = 0;
            let excused = 0;
            let totalRecorded = 0;

            activeTrainings.forEach(t => {
                const s = getStatus(t.id, athlete.id);
                if (s !== 0) totalRecorded++;
                if (s === 1) present++;
                else if (s === 2) absent++;
                else if (s === 3) { present++; late++; }
                else if (s === 5) excused++;
            });

            // Rate based on realized sessions in current view
            const rate = activeRealized.length > 0
                ? Math.round((present / activeRealized.length) * 100)
                : 0;

            // Full season stats for this athlete
            let seasonPresent = 0;
            realizedTrainings.forEach(t => {
                const s = getStatus(t.id, athlete.id);
                if (s === 1 || s === 3) seasonPresent++;
            });
            const seasonRate = seasonRealizedCount > 0
                ? Math.round((seasonPresent / seasonRealizedCount) * 100)
                : 0;

            map[athlete.id] = { present, absent, late, excused, totalRecorded, rate, seasonRate };
        });
        return map;
    }, [athletes, history.trainings, selectedMonth, realizedTrainings, isTrainingRealized, getStatus]);

    const trainingStatsMap = useMemo(() => {
        const map = {};
        history.trainings.forEach(t => {
            let presentCount = 0;
            let recordedCount = 0;
            athletes.forEach(a => {
                const s = getStatus(t.id, a.id);
                if (s === 1 || s === 3) presentCount++;
                if (s !== 0) recordedCount++;
            });
            const total = athletes.length;
            const rate = total > 0 ? Math.round((presentCount / total) * 100) : 0;
            map[t.id] = { presentCount, recordedCount, total, rate };
        });
        return map;
    }, [athletes, history.trainings, getStatus]);

    const overallStats = useMemo(() => {
        const activeTrainings = selectedMonth === 'all'
            ? history.trainings
            : history.trainings.filter(t => {
                const d = new Date(t.startDateTime);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === selectedMonth;
            });

        const activeRealized = activeTrainings.filter(isTrainingRealized);
        const totalSessions = activeRealized.length;
        if (!totalSessions || !athletes.length) return { rate: 0, totalSessions: 0 };
        let totalPossible = totalSessions * athletes.length;
        let totalPresent = 0;

        activeRealized.forEach(t => {
            athletes.forEach(a => {
                const s = getStatus(t.id, a.id);
                if (s === 1 || s === 3) totalPresent++;
            });
        });

        const rate = totalPossible > 0 ? Math.round((totalPresent / totalPossible) * 100) : 0;
        return { rate, totalSessions };
    }, [athletes, history.trainings, selectedMonth, isTrainingRealized, getStatus]);

    const modifiedCount = Object.keys(modifiedRecords).length;

    const handleSave = async () => {
        const changes = Object.keys(modifiedRecords);
        if (changes.length === 0) {
            onClose();
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('token');

            const grouped = {};
            changes.forEach(key => {
                const [eventId, athleteId] = key.split('_').map(Number);
                if (!grouped[eventId]) grouped[eventId] = [];
                grouped[eventId].push({ athleteId, status: modifiedRecords[key], reason: '' });
            });

            const promises = Object.keys(grouped).map(eventId =>
                fetch(`${API_BASE_URL}/api/attendance/batch`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        eventId: Number(eventId),
                        attendances: grouped[eventId]
                    })
                })
            );

            await Promise.all(promises);
            setModifiedRecords({});
            window.dispatchEvent(new CustomEvent('dashboardRefresh'));
            onClose();
        } catch (err) {
            console.error('Error saving changes:', err);
            alert('Erro ao guardar alterações. Por favor, tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    const getAthleteInitials = (athlete) => {
        if (athlete.jerseyNumber) return `#${athlete.jerseyNumber}`;
        const parts = (athlete.name || '').trim().split(' ');
        if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        return (athlete.name || 'A').slice(0, 2).toUpperCase();
    };

    const formatMonthLabel = (mKey) => {
        if (!mKey || mKey === 'all') return 'Todos os Meses';
        const [y, m] = mKey.split('-').map(Number);
        const d = new Date(y, m - 1, 1);
        const monthName = d.toLocaleDateString('pt-PT', { month: 'short' }).replace('.', '');
        return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${y}`;
    };

    const selectedSession = useMemo(() => {
        return history.trainings.find(t => t.id === selectedSessionId) || history.trainings[0] || null;
    }, [history.trainings, selectedSessionId]);

    const handleExportExcel = () => {
        if (!history.trainings || history.trainings.length === 0) {
            alert('Não existem sessões de treino para exportar.');
            return;
        }

        const wb = XLSX.utils.book_new();

        const sortedTrainings = [...history.trainings].sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime));
        const seasonRealizedTrainings = sortedTrainings.filter(t => athletes.some(a => getStatus(t.id, a.id) !== 0));

        // Get unique months in chronological order
        const monthKeys = Array.from(new Set(
            sortedTrainings.map(t => {
                const d = new Date(t.startDateTime);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            })
        )).sort();

        const subtitle = `Equipa: ${teamName || 'Equipa'} | Modalidade: ${sportName || 'Modalidade'} | Época: ${season || 'Época'} | Exportado em: ${new Date().toLocaleDateString('pt-PT')}`;

        // ══════════════════════════════════════════════════════════
        // HELPER: BUILD MATRIX WORKSHEET
        // ══════════════════════════════════════════════════════════
        const buildMatrixSheet = (sheetTitle, sheetSubtitle, sheetTrainings) => {
            const aoa = [];
            const styles = {};
            const merges = [];

            // Row 0: Main title
            aoa.push([sheetTitle]);
            styles['R0C0'] = {
                font: { name: 'Arial', sz: 14, bold: true, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: '003380' } },
                alignment: { horizontal: 'left', vertical: 'center' }
            };

            // Row 1: Subtitle
            aoa.push([sheetSubtitle]);
            styles['R1C0'] = {
                font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '334155' } },
                fill: { fgColor: { rgb: 'F1F5F9' } },
                alignment: { horizontal: 'left', vertical: 'center' }
            };

            // Row 2: Empty spacer
            aoa.push([]);

            // Headers
            const dateHeaders = sheetTrainings.map(t => {
                const d = new Date(t.startDateTime);
                return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
            });

            const headerRow = [
                'Nº',
                'Atleta',
                'Posição',
                'Assiduidade (%)',
                ...dateHeaders,
                'Treinos Realizados',
                'Presenças',
                'Faltas',
                'Atrasos',
                'Justificados'
            ];
            aoa.push(headerRow);
            const headerRowIdx = 3;
            const totalCols = headerRow.length;

            merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } });
            merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } });

            for (let c = 0; c < totalCols; c++) {
                styles[`R${headerRowIdx}C${c}`] = {
                    font: { name: 'Arial', sz: 9, bold: true, color: { rgb: 'FFFFFF' } },
                    fill: { fgColor: { rgb: '003380' } },
                    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                    border: {
                        top: { style: 'thin', color: { rgb: '002255' } },
                        bottom: { style: 'medium', color: { rgb: '001A40' } },
                        left: { style: 'thin', color: { rgb: '1E40AF' } },
                        right: { style: 'thin', color: { rgb: '1E40AF' } }
                    }
                };
            }
            styles[`R${headerRowIdx}C1`].alignment = { horizontal: 'left', vertical: 'center' };

            const sheetRealized = sheetTrainings.filter(t => athletes.some(a => getStatus(t.id, a.id) !== 0));
            const realizedCount = sheetRealized.length;

            athletes.forEach((athlete, aIdx) => {
                const rowIdx = headerRowIdx + 1 + aIdx;
                let present = 0;
                let absent = 0;
                let late = 0;
                let excused = 0;

                const statusCells = sheetTrainings.map(t => {
                    const s = getStatus(t.id, athlete.id);
                    if (s === 1) { present++; return 'P'; }
                    if (s === 2) { absent++; return 'F'; }
                    if (s === 3) { present++; late++; return 'A'; }
                    if (s === 4) return 'L';
                    if (s === 5) { excused++; return 'J'; }
                    return '-';
                });

                const rate = realizedCount > 0 ? Math.round((present / realizedCount) * 100) : 0;

                const rowData = [
                    athlete.jerseyNumber || '',
                    athlete.name || 'Atleta',
                    athlete.position || '',
                    `${rate}%`,
                    ...statusCells,
                    realizedCount,
                    present,
                    absent,
                    late,
                    excused
                ];
                aoa.push(rowData);

                const isEven = aIdx % 2 === 0;
                const rowBg = isEven ? 'FFFFFF' : 'F8FAFC';

                styles[`R${rowIdx}C0`] = {
                    font: { name: 'Arial', sz: 9, color: { rgb: '475569' } },
                    fill: { fgColor: { rgb: rowBg } },
                    alignment: { horizontal: 'center', vertical: 'center' },
                    border: { bottom: { style: 'thin', color: { rgb: 'E2E8F0' } } }
                };

                styles[`R${rowIdx}C1`] = {
                    font: { name: 'Arial', sz: 9, bold: true, color: { rgb: '0F172A' } },
                    fill: { fgColor: { rgb: rowBg } },
                    alignment: { horizontal: 'left', vertical: 'center' },
                    border: { bottom: { style: 'thin', color: { rgb: 'E2E8F0' } } }
                };

                styles[`R${rowIdx}C2`] = {
                    font: { name: 'Arial', sz: 9, color: { rgb: '64748B' } },
                    fill: { fgColor: { rgb: rowBg } },
                    alignment: { horizontal: 'left', vertical: 'center' },
                    border: { bottom: { style: 'thin', color: { rgb: 'E2E8F0' } } }
                };

                const rateColor = rate >= 80 ? '15803D' : rate >= 60 ? 'B45309' : 'B91C1C';
                const rateBg = rate >= 80 ? 'DCFCE7' : rate >= 60 ? 'FEF3C7' : 'FEE2E2';
                styles[`R${rowIdx}C3`] = {
                    font: { name: 'Arial', sz: 9, bold: true, color: { rgb: rateColor } },
                    fill: { fgColor: { rgb: rateBg } },
                    alignment: { horizontal: 'center', vertical: 'center' },
                    border: { bottom: { style: 'thin', color: { rgb: 'E2E8F0' } } }
                };

                statusCells.forEach((code, cIdx) => {
                    const colIdx = 4 + cIdx;
                    let fg = '64748B';
                    let bg = rowBg;
                    let bold = false;
                    if (code === 'P') { fg = '15803D'; bg = 'DCFCE7'; bold = true; }
                    else if (code === 'F') { fg = 'B91C1C'; bg = 'FEE2E2'; bold = true; }
                    else if (code === 'A') { fg = 'B45309'; bg = 'FEF3C7'; bold = true; }
                    else if (code === 'J') { fg = '0369A1'; bg = 'E0F2FE'; bold = true; }
                    else if (code === 'L') { fg = '475569'; bg = 'F1F5F9'; }

                    styles[`R${rowIdx}C${colIdx}`] = {
                        font: { name: 'Arial', sz: 9, bold, color: { rgb: fg } },
                        fill: { fgColor: { rgb: bg } },
                        alignment: { horizontal: 'center', vertical: 'center' },
                        border: {
                            bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
                            left: { style: 'thin', color: { rgb: 'F1F5F9' } },
                            right: { style: 'thin', color: { rgb: 'F1F5F9' } }
                        }
                    };
                });

                const startSummaryIdx = 4 + statusCells.length;
                [realizedCount, present, absent, late, excused].forEach((val, sIdx) => {
                    const colIdx = startSummaryIdx + sIdx;
                    styles[`R${rowIdx}C${colIdx}`] = {
                        font: { name: 'Arial', sz: 9, bold: sIdx < 2, color: { rgb: '1E293B' } },
                        fill: { fgColor: { rgb: rowBg } },
                        alignment: { horizontal: 'center', vertical: 'center' },
                        border: {
                            bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
                            left: { style: 'thin', color: { rgb: 'E2E8F0' } }
                        }
                    };
                });
            });

            // Summary row at bottom
            const summaryRowIdx = headerRowIdx + 1 + athletes.length;
            const sessionRates = sheetTrainings.map(t => {
                let pCount = 0;
                athletes.forEach(a => {
                    const s = getStatus(t.id, a.id);
                    if (s === 1 || s === 3) pCount++;
                });
                return athletes.length > 0 ? `${Math.round((pCount / athletes.length) * 100)}%` : '0%';
            });

            let totalPresentInSheet = 0;
            let totalPossibleInSheet = realizedCount * athletes.length;
            sheetRealized.forEach(t => {
                athletes.forEach(a => {
                    const s = getStatus(t.id, a.id);
                    if (s === 1 || s === 3) totalPresentInSheet++;
                });
            });
            const sheetTeamRate = totalPossibleInSheet > 0 ? Math.round((totalPresentInSheet / totalPossibleInSheet) * 100) : 0;

            const summaryRowData = [
                '',
                'TAXA DE PRESENÇA NO TREINO (%)',
                '',
                `${sheetTeamRate}%`,
                ...sessionRates,
                '', '', '', '', ''
            ];
            aoa.push(summaryRowData);
            merges.push({ s: { r: summaryRowIdx, c: 1 }, e: { r: summaryRowIdx, c: 2 } });

            for (let c = 0; c < totalCols; c++) {
                styles[`R${summaryRowIdx}C${c}`] = {
                    font: { name: 'Arial', sz: 9, bold: true, color: { rgb: '003380' } },
                    fill: { fgColor: { rgb: 'E2E8F0' } },
                    alignment: { horizontal: 'center', vertical: 'center' },
                    border: {
                        top: { style: 'medium', color: { rgb: '94A3B8' } },
                        bottom: { style: 'medium', color: { rgb: '94A3B8' } }
                    }
                };
            }
            styles[`R${summaryRowIdx}C1`].alignment = { horizontal: 'left', vertical: 'center' };

            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws['!merges'] = merges;
            ws['!cols'] = [
                { wch: 6 },   // Nº
                { wch: 28 },  // Atleta
                { wch: 14 },  // Posição
                { wch: 16 },  // Taxa %
                ...sheetTrainings.map(() => ({ wch: 7 })), // Dates
                { wch: 18 },  // Treinos Realizados
                { wch: 12 },  // Presenças
                { wch: 10 },  // Faltas
                { wch: 10 },  // Atrasos
                { wch: 13 }   // Justificados
            ];
            ws['!rows'] = aoa.map((_, i) => ({
                hpt: i === 0 ? 28 : i === 1 ? 20 : i === headerRowIdx ? 26 : 20
            }));

            for (const [key, style] of Object.entries(styles)) {
                const m = key.match(/R(\d+)C(\d+)/);
                if (!m) continue;
                const r = parseInt(m[1]), c = parseInt(m[2]);
                const addr = XLSX.utils.encode_cell({ r, c });
                if (ws[addr]) ws[addr].s = style;
                else ws[addr] = { t: 's', v: '', s: style };
            }

            return ws;
        };

        // ══════════════════════════════════════════════════════════
        // FOLHA 1: RESUMO GERAL COM COLUNAS MENSAIS E TOTAIS
        // ══════════════════════════════════════════════════════════
        const aoaSummary = [];
        const summaryStyles = {};
        const summaryMerges = [];

        aoaSummary.push(['CLUBE DESPORTIVO DA PÓVOA — RESUMO ESTATÍSTICO DE ASSIDUIDADE']);
        summaryStyles['R0C0'] = {
            font: { name: 'Arial', sz: 14, bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '003380' } },
            alignment: { horizontal: 'left', vertical: 'center' }
        };

        aoaSummary.push([subtitle]);
        summaryStyles['R1C0'] = {
            font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '334155' } },
            fill: { fgColor: { rgb: 'F1F5F9' } },
            alignment: { horizontal: 'left', vertical: 'center' }
        };

        aoaSummary.push([]);

        // Base summary columns
        const baseHeaders = [
            'Nº',
            'Nome do Atleta',
            'Posição',
            'Sessões Realizadas',
            'Presenças',
            'Faltas',
            'Atrasos',
            'Justificadas',
            'Assiduidade Global (%)',
            'Avaliação Global'
        ];

        // Format short month label, e.g. "% Out 26"
        const formatShortMonthCol = (mKey) => {
            const [y, m] = mKey.split('-').map(Number);
            const d = new Date(y, m - 1, 1);
            const monthShort = d.toLocaleDateString('pt-PT', { month: 'short' }).replace('.', '');
            const cap = monthShort.charAt(0).toUpperCase() + monthShort.slice(1);
            return `% ${cap} ${String(y).slice(-2)}`;
        };

        const monthHeaders = monthKeys.map(formatShortMonthCol);
        const fullSummaryHeaders = [...baseHeaders, ...monthHeaders];
        aoaSummary.push(fullSummaryHeaders);

        const sHeaderIdx = 3;
        const totalSummaryCols = fullSummaryHeaders.length;

        summaryMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalSummaryCols - 1 } });
        summaryMerges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: totalSummaryCols - 1 } });

        for (let c = 0; c < totalSummaryCols; c++) {
            summaryStyles[`R${sHeaderIdx}C${c}`] = {
                font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: c >= baseHeaders.length ? '1E40AF' : '003380' } },
                alignment: { horizontal: c === 1 ? 'left' : 'center', vertical: 'center' },
                border: {
                    top: { style: 'thin', color: { rgb: '002255' } },
                    bottom: { style: 'medium', color: { rgb: '001A40' } },
                    left: { style: 'thin', color: { rgb: '1E3A8A' } },
                    right: { style: 'thin', color: { rgb: '1E3A8A' } }
                }
            };
        }

        const totalSeasonRealized = seasonRealizedTrainings.length;

        // Precompute month realized sessions for fast lookup
        const monthRealizedMap = {};
        monthKeys.forEach(mKey => {
            monthRealizedMap[mKey] = sortedTrainings.filter(t => {
                const d = new Date(t.startDateTime);
                const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                return k === mKey && athletes.some(a => getStatus(t.id, a.id) !== 0);
            });
        });

        athletes.forEach((athlete, aIdx) => {
            const rIdx = sHeaderIdx + 1 + aIdx;

            let seasonPresent = 0;
            let seasonAbsent = 0;
            let seasonLate = 0;
            let seasonExcused = 0;

            sortedTrainings.forEach(t => {
                const s = getStatus(t.id, athlete.id);
                if (s === 1) seasonPresent++;
                else if (s === 2) seasonAbsent++;
                else if (s === 3) { seasonPresent++; seasonLate++; }
                else if (s === 5) seasonExcused++;
            });

            const seasonRate = totalSeasonRealized > 0
                ? Math.round((seasonPresent / totalSeasonRealized) * 100)
                : 0;

            let avaliacao = 'Sem Registos';
            let avalBg = 'F1F5F9';
            let avalFg = '475569';
            if (totalSeasonRealized > 0) {
                if (seasonRate >= 85) { avaliacao = 'Excelente'; avalBg = 'DCFCE7'; avalFg = '15803D'; }
                else if (seasonRate >= 70) { avaliacao = 'Bom'; avalBg = 'E0F2FE'; avalFg = '0369A1'; }
                else if (seasonRate >= 50) { avaliacao = 'Regular'; avalBg = 'FEF3C7'; avalFg = 'B45309'; }
                else { avaliacao = 'Insuficiente'; avalBg = 'FEE2E2'; avalFg = 'B91C1C'; }
            }

            const monthRateValues = monthKeys.map(mKey => {
                const mRealized = monthRealizedMap[mKey];
                if (!mRealized || mRealized.length === 0) return '-';

                let mPresent = 0;
                mRealized.forEach(t => {
                    const s = getStatus(t.id, athlete.id);
                    if (s === 1 || s === 3) mPresent++;
                });

                const mRate = Math.round((mPresent / mRealized.length) * 100);
                return `${mRate}%`;
            });

            const sRow = [
                athlete.jerseyNumber || '',
                athlete.name || 'Atleta',
                athlete.position || '',
                totalSeasonRealized,
                seasonPresent,
                seasonAbsent,
                seasonLate,
                seasonExcused,
                totalSeasonRealized > 0 ? `${seasonRate}%` : '-',
                avaliacao,
                ...monthRateValues
            ];
            aoaSummary.push(sRow);

            const isEven = aIdx % 2 === 0;
            const bg = isEven ? 'FFFFFF' : 'F8FAFC';

            for (let c = 0; c < totalSummaryCols; c++) {
                summaryStyles[`R${rIdx}C${c}`] = {
                    font: { name: 'Arial', sz: 9, color: { rgb: '1E293B' } },
                    fill: { fgColor: { rgb: bg } },
                    alignment: { horizontal: c === 1 || c === 2 ? 'left' : 'center', vertical: 'center' },
                    border: { bottom: { style: 'thin', color: { rgb: 'E2E8F0' } } }
                };
            }

            summaryStyles[`R${rIdx}C1`].font = { name: 'Arial', sz: 9, bold: true, color: { rgb: '0F172A' } };

            // Global rate cell
            summaryStyles[`R${rIdx}C8`] = {
                font: { name: 'Arial', sz: 9, bold: true, color: { rgb: avalFg } },
                fill: { fgColor: { rgb: avalBg } },
                alignment: { horizontal: 'center', vertical: 'center' },
                border: { bottom: { style: 'thin', color: { rgb: 'E2E8F0' } } }
            };

            // Avaliação cell
            summaryStyles[`R${rIdx}C9`] = {
                font: { name: 'Arial', sz: 9, bold: true, color: { rgb: avalFg } },
                fill: { fgColor: { rgb: avalBg } },
                alignment: { horizontal: 'center', vertical: 'center' },
                border: { bottom: { style: 'thin', color: { rgb: 'E2E8F0' } } }
            };

            // Monthly rate cells styling
            monthRateValues.forEach((val, mIdx) => {
                const cIdx = baseHeaders.length + mIdx;
                if (val !== '-') {
                    const numRate = parseInt(val, 10);
                    const fg = numRate >= 80 ? '15803D' : numRate >= 60 ? 'B45309' : 'B91C1C';
                    const fill = isEven ? 'F8FAFC' : 'F1F5F9';
                    summaryStyles[`R${rIdx}C${cIdx}`] = {
                        font: { name: 'Arial', sz: 9, bold: true, color: { rgb: fg } },
                        fill: { fgColor: { rgb: fill } },
                        alignment: { horizontal: 'center', vertical: 'center' },
                        border: {
                            bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
                            left: { style: 'thin', color: { rgb: 'E2E8F0' } }
                        }
                    };
                } else {
                    summaryStyles[`R${rIdx}C${cIdx}`] = {
                        font: { name: 'Arial', sz: 9, color: { rgb: '94A3B8' } },
                        fill: { fgColor: { rgb: bg } },
                        alignment: { horizontal: 'center', vertical: 'center' },
                        border: {
                            bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
                            left: { style: 'thin', color: { rgb: 'E2E8F0' } }
                        }
                    };
                }
            });
        });

        // Summary row at bottom of Sheet 1
        const sSummaryRIdx = sHeaderIdx + 1 + athletes.length;
        let totalP = 0, totalF = 0, totalA = 0, totalJ = 0;
        athletes.forEach(a => {
            sortedTrainings.forEach(t => {
                const s = getStatus(t.id, a.id);
                if (s === 1) totalP++;
                else if (s === 2) totalF++;
                else if (s === 3) { totalP++; totalA++; }
                else if (s === 5) totalJ++;
            });
        });

        const totalSeasonPossible = totalSeasonRealized * athletes.length;
        let totalSeasonPresentCount = 0;
        seasonRealizedTrainings.forEach(t => {
            athletes.forEach(a => {
                const s = getStatus(t.id, a.id);
                if (s === 1 || s === 3) totalSeasonPresentCount++;
            });
        });
        const teamSeasonRate = totalSeasonPossible > 0 ? Math.round((totalSeasonPresentCount / totalSeasonPossible) * 100) : 0;

        const monthTeamRates = monthKeys.map(mKey => {
            const mRealized = monthRealizedMap[mKey];
            if (!mRealized || mRealized.length === 0) return '-';
            const mPossible = mRealized.length * athletes.length;
            let mPresent = 0;
            mRealized.forEach(t => {
                athletes.forEach(a => {
                    const s = getStatus(t.id, a.id);
                    if (s === 1 || s === 3) mPresent++;
                });
            });
            return mPossible > 0 ? `${Math.round((mPresent / mPossible) * 100)}%` : '0%';
        });

        const sSummaryRow = [
            '',
            'TOTAL / MÉDIA DA EQUIPA',
            '',
            totalSeasonRealized,
            totalP,
            totalF,
            totalA,
            totalJ,
            totalSeasonRealized > 0 ? `${teamSeasonRate}%` : '-',
            teamSeasonRate >= 75 ? 'Excelente' : teamSeasonRate >= 60 ? 'Bom' : 'Atenção',
            ...monthTeamRates
        ];
        aoaSummary.push(sSummaryRow);
        summaryMerges.push({ s: { r: sSummaryRIdx, c: 1 }, e: { r: sSummaryRIdx, c: 2 } });

        for (let c = 0; c < totalSummaryCols; c++) {
            summaryStyles[`R${sSummaryRIdx}C${c}`] = {
                font: { name: 'Arial', sz: 10, bold: true, color: { rgb: '003380' } },
                fill: { fgColor: { rgb: 'E2E8F0' } },
                alignment: { horizontal: c === 1 ? 'left' : 'center', vertical: 'center' },
                border: {
                    top: { style: 'medium', color: { rgb: '94A3B8' } },
                    bottom: { style: 'medium', color: { rgb: '94A3B8' } }
                }
            };
        }

        const wsSummary = XLSX.utils.aoa_to_sheet(aoaSummary);
        wsSummary['!merges'] = summaryMerges;
        wsSummary['!cols'] = [
            { wch: 6 },   // Nº
            { wch: 28 },  // Atleta
            { wch: 16 },  // Posição
            { wch: 18 },  // Sessões Realizadas
            { wch: 12 },  // Presenças
            { wch: 10 },  // Faltas
            { wch: 10 },  // Atrasos
            { wch: 14 },  // Justificadas
            { wch: 22 },  // Assiduidade Global (%)
            { wch: 16 },  // Avaliação Global
            ...monthKeys.map(() => ({ wch: 12 })) // Colunas Mensais
        ];
        wsSummary['!rows'] = aoaSummary.map((_, i) => ({
            hpt: i === 0 ? 28 : i === 1 ? 20 : i === sHeaderIdx ? 26 : 20
        }));

        for (const [key, style] of Object.entries(summaryStyles)) {
            const m = key.match(/R(\d+)C(\d+)/);
            if (!m) continue;
            const r = parseInt(m[1]), c = parseInt(m[2]);
            const addr = XLSX.utils.encode_cell({ r, c });
            if (wsSummary[addr]) wsSummary[addr].s = style;
            else wsSummary[addr] = { t: 's', v: '', s: style };
        }

        // ══════════════════════════════════════════════════════════
        // ADD SHEETS TO WORKBOOK IN USER-CONFIRMED ORDER:
        // 1. Resumo Geral
        // 2. Abas mensais
        // 3. Matriz Época
        // ══════════════════════════════════════════════════════════
        XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo Geral');

        monthKeys.forEach(mKey => {
            const monthTrainings = sortedTrainings.filter(t => {
                const d = new Date(t.startDateTime);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === mKey;
            });

            if (monthTrainings.length > 0) {
                const [y, m] = mKey.split('-').map(Number);
                const d = new Date(y, m - 1, 1);
                const monthLong = d.toLocaleDateString('pt-PT', { month: 'long' });
                const capMonth = monthLong.charAt(0).toUpperCase() + monthLong.slice(1);
                const sheetName = `${capMonth} ${y}`.slice(0, 31);

                const wsMonth = buildMatrixSheet(
                    `CLUBE DESPORTIVO DA PÓVOA — REGISTO DE PRESENÇAS — ${capMonth.toUpperCase()} ${y}`,
                    `Equipa: ${teamName || 'Equipa'} | Modalidade: ${sportName || 'Modalidade'} | Época: ${season || 'Época'} | Mês: ${capMonth} ${y}`,
                    monthTrainings
                );
                XLSX.utils.book_append_sheet(wb, wsMonth, sheetName);
            }
        });

        const wsSeasonMatrix = buildMatrixSheet(
            'CLUBE DESPORTIVO DA PÓVOA — REGISTO DE PRESENÇAS — ÉPOCA COMPLETA',
            subtitle,
            sortedTrainings
        );
        XLSX.utils.book_append_sheet(wb, wsSeasonMatrix, 'Matriz Época');

        // ══════════════════════════════════════════════════════════
        // GRAVAÇÃO E DOWNLOAD
        // ══════════════════════════════════════════════════════════
        const safeTeam = (teamName || 'Equipa').replace(/[^a-zA-Z0-9_-]/g, '_');
        const todayStr = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `CDP_Assiduidade_${safeTeam}_${todayStr}.xlsx`);
    };

    if (!isOpen && !embedded) return null;

    const modalContent = (
        <div className={`attendance-history-modal ${embedded ? 'embedded' : ''}`} onClick={e => e.stopPropagation()}>
            {/* ── HEADER ── */}
            <header className="attendance-modal-header">
                <div className="header-left">
                    <div className="header-icon-badge">
                        <Calendar size={24} />
                    </div>
                    <div className="header-titles">
                        <div className="title-row">
                            <h2>Histórico de Assiduidade</h2>
                            {teamName && <span className="team-pill">{teamName}</span>}
                            {season && <span className="season-pill">{season}</span>}
                        </div>
                        <p className="subtitle">
                            Registo, estatísticas e controlo de presenças nos treinos
                        </p>
                    </div>
                </div>

                <div className="header-right">
                    <button
                        className="btn-export-excel"
                        onClick={handleExportExcel}
                        title="Exportar para Excel (.xlsx)"
                    >
                        <FileSpreadsheet size={16} />
                        <span>Exportar Excel</span>
                    </button>

                    <div className="overall-stat-card">
                        <div className="stat-value">
                            <TrendingUp size={16} />
                            <span>{overallStats.rate}%</span>
                        </div>
                        <span className="stat-label">Assiduidade Geral</span>
                    </div>

                    {!embedded && (
                        <button className="modal-close-button" onClick={onClose} aria-label="Fechar">
                            <X size={20} />
                        </button>
                    )}
                </div>
            </header>

                {/* ── TOOLBAR & CONTROLS ── */}
                <div className="attendance-toolbar">
                    <div className="toolbar-top">
                        {/* View mode switcher */}
                        <div className="view-mode-tabs">
                            <button
                                className={`view-tab ${viewMode === 'matrix' ? 'active' : ''}`}
                                onClick={() => setViewMode('matrix')}
                                title="Vista Grelha Completa"
                            >
                                <LayoutGrid size={16} />
                                <span>Grelha Matriz</span>
                            </button>
                            <button
                                className={`view-tab ${viewMode === 'session' ? 'active' : ''}`}
                                onClick={() => setViewMode('session')}
                                title="Vista por Treino (Mobile-Friendly)"
                            >
                                <CalendarDays size={16} />
                                <span>Por Treino</span>
                            </button>
                            <button
                                className={`view-tab ${viewMode === 'athletes' ? 'active' : ''}`}
                                onClick={() => setViewMode('athletes')}
                                title="Resumo Individual por Atleta"
                            >
                                <UserCheck size={16} />
                                <span>Atletas ({athletes.length})</span>
                            </button>
                        </div>

                        {/* Search bar */}
                        <div className="toolbar-search">
                            <Search size={16} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Pesquisar atleta..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Month Filter Chips */}
                    {availableMonths.length > 1 && (
                        <div className="month-filter-row">
                            <button
                                className={`month-chip ${selectedMonth === 'all' ? 'active' : ''}`}
                                onClick={() => setSelectedMonth('all')}
                            >
                                Todos ({history.trainings.length})
                            </button>
                            {availableMonths.map(mKey => {
                                const count = history.trainings.filter(t => {
                                    const d = new Date(t.startDateTime);
                                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === mKey;
                                }).length;

                                return (
                                    <button
                                        key={mKey}
                                        className={`month-chip ${selectedMonth === mKey ? 'active' : ''}`}
                                        onClick={() => setSelectedMonth(mKey)}
                                    >
                                        {formatMonthLabel(mKey)} ({count})
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ── MODAL BODY CONTENT ── */}
                <div className="attendance-modal-body">
                    {loading ? (
                        <div className="attendance-loading-state">
                            <Loader2 size={36} className="spinner" />
                            <span>A carregar histórico da equipa...</span>
                        </div>
                    ) : history.trainings.length === 0 ? (
                        <div className="attendance-empty-state">
                            <Calendar size={48} />
                            <h3>Nenhum treino registado</h3>
                            <p>Ainda não foram criados treinos no calendário para esta equipa.</p>
                        </div>
                    ) : (
                        <>
                            {/* ── 1. MATRIX VIEW ── */}
                            {viewMode === 'matrix' && (
                                <div className="matrix-view-wrapper">
                                    <div className="history-table-container" ref={containerRef}>
                                        <table className="history-table">
                                            <thead>
                                                <tr>
                                                    <th className="sticky-col athlete-col-header">
                                                        <div className="header-cell">
                                                            <Users size={15} />
                                                            <span>Atleta</span>
                                                            <span className="athlete-count-badge">{filteredAthletes.length}</span>
                                                        </div>
                                                    </th>
                                                    <th className="sticky-stat-col stat-header-col">
                                                        <span>Taxa %</span>
                                                    </th>
                                                    {filteredTrainings.map(t => {
                                                        const stat = trainingStatsMap[t.id] || { rate: 0, presentCount: 0, total: 0 };
                                                        const d = new Date(t.startDateTime);
                                                        const isToday = new Date().toDateString() === d.toDateString();

                                                        return (
                                                            <th key={t.id} className={`training-col-header ${isToday ? 'is-today' : ''}`}>
                                                                <div className="training-header-content">
                                                                    <div className="date-pill">
                                                                        <span className="weekday">{d.toLocaleDateString('pt-PT', { weekday: 'short' }).replace('.', '').toUpperCase()}</span>
                                                                        <span className="day-month">{d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}</span>
                                                                    </div>

                                                                    <div className="training-meta">
                                                                        <span className="session-title" title={t.title}>
                                                                            {t.title || 'Treino'}
                                                                        </span>
                                                                        <div className="session-rate-badge" title={`${stat.presentCount} de ${stat.total} atletas presentes`}>
                                                                            <span className={`rate-dot ${stat.rate >= 75 ? 'high' : stat.rate >= 50 ? 'med' : 'low'}`} />
                                                                            <span>{stat.rate}%</span>
                                                                        </div>
                                                                    </div>

                                                                    {!readOnly && (
                                                                        <button
                                                                            className="quick-all-btn"
                                                                            onClick={() => handleMarkAllPresent(t.id)}
                                                                            title="Marcar todos como Presentes"
                                                                        >
                                                                            <CheckCheck size={12} />
                                                                            <span>Todos</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </th>
                                                        );
                                                    })}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredAthletes.map(athlete => {
                                                    const aStat = athleteStatsMap[athlete.id] || { rate: 0, present: 0 };

                                                    return (
                                                        <tr key={athlete.id} className="athlete-row">
                                                            <td className="sticky-col athlete-name-cell">
                                                                <div className="athlete-cell-content">
                                                                    <div className="athlete-avatar-mini">
                                                                        {getAthleteInitials(athlete)}
                                                                    </div>
                                                                    <div className="athlete-text">
                                                                        <span className="athlete-name">{athlete.name}</span>
                                                                        {athlete.position && <span className="athlete-pos">{athlete.position}</span>}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td className="sticky-stat-col athlete-rate-cell">
                                                                <div className={`rate-badge ${aStat.rate >= 80 ? 'good' : aStat.rate >= 60 ? 'warn' : 'bad'}`}>
                                                                    {aStat.rate}%
                                                                </div>
                                                            </td>

                                                            {filteredTrainings.map(t => {
                                                                const status = getStatus(t.id, athlete.id);
                                                                const isModified = isRecordModified(t.id, athlete.id);
                                                                const conf = STATUS_CONFIG[status] || STATUS_CONFIG[0];
                                                                const IconComp = conf.icon;

                                                                return (
                                                                    <td
                                                                        key={`${t.id}_${athlete.id}`}
                                                                        className={`status-cell status-val-${status} ${isModified ? 'is-modified' : ''} ${readOnly ? 'is-readonly' : ''}`}
                                                                        onClick={() => !readOnly && handleStatusCycle(t.id, athlete.id)}
                                                                        title={readOnly ? `${athlete.name} — ${conf.label}` : `${athlete.name} — ${conf.label} (Clique para alternar)`}
                                                                    >
                                                                        <div className={`status-indicator-box ${conf.className}`}>
                                                                            {IconComp ? (
                                                                                <IconComp size={16} strokeWidth={2.5} />
                                                                            ) : (
                                                                                <span className="empty-indicator-dot" />
                                                                            )}
                                                                            {isModified && <span className="modified-dot" title="Alteração pendente" />}
                                                                        </div>
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* ── 2. SESSION VIEW (MOBILE OPTIMIZED) ── */}
                            {viewMode === 'session' && (
                                <div className="session-view-wrapper">
                                    {/* Horizontal session picker */}
                                    <div className="session-carousel-container" ref={sessionScrollRef}>
                                        <div className="session-carousel">
                                            {filteredTrainings.map(t => {
                                                const d = new Date(t.startDateTime);
                                                const isSelected = selectedSessionId === t.id;
                                                const stat = trainingStatsMap[t.id] || { rate: 0, presentCount: 0 };
                                                const isToday = new Date().toDateString() === d.toDateString();

                                                return (
                                                    <button
                                                        key={t.id}
                                                        className={`session-card-btn ${isSelected ? 'active' : ''} ${isToday ? 'today' : ''}`}
                                                        onClick={() => setSelectedSessionId(t.id)}
                                                    >
                                                        <span className="sc-day">{d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}</span>
                                                        <span className="sc-title">{t.title || 'Treino'}</span>
                                                        <div className="sc-rate">
                                                            <span>{stat.rate}%</span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Active Session Detail */}
                                    {selectedSession && (
                                        <div className="session-detail-container">
                                            <div className="session-overview-card">
                                                <div className="soc-left">
                                                    <div className="soc-date-badge">
                                                        <Calendar size={18} />
                                                        <span>{new Date(selectedSession.startDateTime).toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</span>
                                                    </div>
                                                    <h4>{selectedSession.title || 'Sessão de Treino'}</h4>
                                                </div>

                                                <div className="soc-actions">
                                                    <div className="session-progress-summary">
                                                        <span className="present-count">
                                                            {trainingStatsMap[selectedSession.id]?.presentCount || 0}/{athletes.length} Presentes
                                                        </span>
                                                        <div className="session-bar-track">
                                                            <div
                                                                className="session-bar-fill"
                                                                style={{ width: `${trainingStatsMap[selectedSession.id]?.rate || 0}%` }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {!readOnly && (
                                                        <button
                                                            className="btn-mark-all-session"
                                                            onClick={() => handleMarkAllPresent(selectedSession.id)}
                                                        >
                                                            <CheckCheck size={16} />
                                                            <span>Marcar Todos Presentes</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Athletes interactive list */}
                                            <div className="session-athletes-list">
                                                {filteredAthletes.map(athlete => {
                                                    const status = getStatus(selectedSession.id, athlete.id);
                                                    const isModified = isRecordModified(selectedSession.id, athlete.id);
                                                    const aStat = athleteStatsMap[athlete.id];

                                                    return (
                                                        <div key={athlete.id} className={`session-athlete-item ${isModified ? 'is-modified-item' : ''}`}>
                                                            <div className="sai-info">
                                                                <div className="sai-avatar">
                                                                    {getAthleteInitials(athlete)}
                                                                </div>
                                                                <div className="sai-name-group">
                                                                    <span className="sai-name">{athlete.name}</span>
                                                                    <span className="sai-sub">
                                                                        {athlete.position || 'Atleta'} • Assiduidade: <strong>{aStat?.rate || 0}%</strong>
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* 1-Tap Status Selector for Mobile */}
                                                            <div className="sai-actions">
                                                                {[
                                                                    { val: 1, label: 'Presente', icon: Check, class: 'btn-pres' },
                                                                    { val: 2, label: 'Falta', icon: X, class: 'btn-abs' },
                                                                    { val: 3, label: 'Atraso', icon: Clock, class: 'btn-late' },
                                                                    { val: 5, label: 'Justificado', icon: FileText, class: 'btn-exc' },
                                                                ].map(btn => {
                                                                    const isActive = status === btn.val;
                                                                    const IconC = btn.icon;
                                                                    return (
                                                                        <button
                                                                            key={btn.val}
                                                                            className={`sai-status-pill ${btn.class} ${isActive ? 'active' : ''} ${readOnly ? 'is-readonly' : ''}`}
                                                                            onClick={() => !readOnly && handleSetExplicitStatus(selectedSession.id, athlete.id, btn.val)}
                                                                            title={readOnly ? btn.label : btn.label}
                                                                            disabled={readOnly}
                                                                        >
                                                                            <IconC size={15} />
                                                                            <span className="pill-label">{btn.label}</span>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── 3. ATHLETES SUMMARY VIEW ── */}
                            {viewMode === 'athletes' && (
                                <div className="athletes-summary-grid">
                                    {filteredAthletes.map(athlete => {
                                        const stat = athleteStatsMap[athlete.id] || { present: 0, absent: 0, late: 0, excused: 0, rate: 0 };

                                        return (
                                            <div key={athlete.id} className="athlete-summary-card">
                                                <div className="asc-top">
                                                    <div className="asc-avatar">
                                                        {getAthleteInitials(athlete)}
                                                    </div>
                                                    <div className="asc-meta">
                                                        <h4>{athlete.name}</h4>
                                                        <span>{athlete.position || 'Atleta'} {athlete.jerseyNumber ? `• #${athlete.jerseyNumber}` : ''}</span>
                                                    </div>
                                                    <div className={`asc-rate-pill ${stat.rate >= 80 ? 'good' : stat.rate >= 60 ? 'warn' : 'bad'}`}>
                                                        {stat.rate}%
                                                    </div>
                                                </div>

                                                <div className="asc-progress">
                                                    <div className="asc-progress-bar">
                                                        <div
                                                            className="asc-progress-fill"
                                                            style={{
                                                                width: `${stat.rate}%`,
                                                                background: stat.rate >= 80 ? '#10b981' : stat.rate >= 60 ? '#f59e0b' : '#ef4444'
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="asc-stats-row">
                                                    <div className="asc-stat-item present">
                                                        <span className="stat-num">{stat.present}</span>
                                                        <span className="stat-lbl">Presenças</span>
                                                    </div>
                                                    <div className="asc-stat-item absent">
                                                        <span className="stat-num">{stat.absent}</span>
                                                        <span className="stat-lbl">Faltas</span>
                                                    </div>
                                                    <div className="asc-stat-item late">
                                                        <span className="stat-num">{stat.late}</span>
                                                        <span className="stat-lbl">Atrasos</span>
                                                    </div>
                                                    <div className="asc-stat-item excused">
                                                        <span className="stat-num">{stat.excused}</span>
                                                        <span className="stat-lbl">Justificadas</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── FOOTER & ACTIONS ── */}
                <footer className="attendance-modal-footer">
                    <div className="footer-legend-group">
                        <div className="legend-chip">
                            <span className="legend-dot present" />
                            <span>Presente</span>
                        </div>
                        <div className="legend-chip">
                            <span className="legend-dot absent" />
                            <span>Falta</span>
                        </div>
                        <div className="legend-chip">
                            <span className="legend-dot late" />
                            <span>Atraso</span>
                        </div>
                        <div className="legend-chip">
                            <span className="legend-dot excused" />
                            <span>Justificado</span>
                        </div>
                    </div>

                    <div className="footer-action-buttons">
                        {readOnly ? (
                            <>
                                <div className="readonly-badge">
                                    <ShieldAlert size={14} /> Modo Leitura (Apenas Treinadores podem editar presenças)
                                </div>
                                {!embedded && (
                                    <button className="btn-footer-cancel" onClick={onClose}>
                                        Fechar
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="changes-tracker">
                                    {modifiedCount > 0 ? (
                                        <span className="modified-badge">
                                            <Activity size={14} />
                                            {modifiedCount} {modifiedCount === 1 ? 'alteração não guardada' : 'alterações não guardadas'}
                                        </span>
                                    ) : (
                                        <span className="saved-badge">
                                            <Check size={14} /> Todas as alterações guardadas
                                        </span>
                                    )}
                                </div>

                                <button className="btn-footer-cancel" onClick={onClose} disabled={saving}>
                                    {modifiedCount > 0 ? 'Descartar' : 'Fechar'}
                                </button>

                                <button
                                    className={`btn-footer-save ${modifiedCount > 0 ? 'pulse' : ''}`}
                                    onClick={handleSave}
                                    disabled={saving || (!loading && modifiedCount === 0)}
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 size={18} className="spinner-small" />
                                            <span>A guardar...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Save size={18} />
                                            <span>Guardar Alterações {modifiedCount > 0 ? `(${modifiedCount})` : ''}</span>
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </footer>
            </div>
    );

    if (embedded) {
        return modalContent;
    }

    return (
        <div className="attendance-modal-overlay" onClick={onClose}>
            {modalContent}
        </div>
    );
};

export default TeamAttendanceHistoryModal;
